import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Button, Card, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FiUploadCloud, FiX, FiRefreshCw, FiCheckCircle, FiAlertCircle, FiPause, FiPlay, FiFileText } from 'react-icons/fi';
import { parseUserInfo } from '../../utilities/common';
import { generateAutoBillUploadApi } from './billParsingApi';
import { uploadToS3WithProgress } from '../../utilities/apiHelper';

const FILE_STATUS = {
  queued: 'queued',
  uploading: 'uploading',
  paused: 'paused',
  done: 'done',
  error: 'error'
};

const CONCURRENT_LIMIT = 1;

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const createFileItem = (file) => ({
  id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
  file,
  status: FILE_STATUS.queued,
  progress: 0,
  error: null,
  abortController: null
});

const BulkUploadTab = () => {
  const { t } = useTranslation();
  const { orgId } = parseUserInfo(useSelector((state) => state.users.user));
  const [fileItems, setFileItems] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const uploadingCountRef = useRef(0);
  const queueRef = useRef([]);
  const abortControllersRef = useRef(new Map());
  const [isStarted, setIsStarted] = useState(false);
  const isStartedRef = useRef(false);

  const uploadFileRef = useRef(null);
  const processQueueRef = useRef(null);

  const updateItem = useCallback((id, patch) => {
    setFileItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, []);

  uploadFileRef.current = async (item) => {
    uploadingCountRef.current += 1;
    const abortController = new AbortController();
    abortControllersRef.current.set(item.id, abortController);
    updateItem(item.id, { status: FILE_STATUS.uploading, progress: 0, error: null });

    try {
      const signedPost = await generateAutoBillUploadApi(orgId, item.file.name);
      if (abortController.signal.aborted) throw new DOMException('Upload cancelled', 'AbortError');
      const keyTemplate = signedPost.fields.key;
      signedPost.fields.key = keyTemplate.replace(/\$\{filename\}/g, item.file.name);

      await uploadToS3WithProgress(
        signedPost,
        item.file,
        (progress) => updateItem(item.id, { progress }),
        abortController.signal
      );
      abortControllersRef.current.delete(item.id);
      updateItem(item.id, { status: FILE_STATUS.done, progress: 100 });
    } catch (err) {
      abortControllersRef.current.delete(item.id);
      if (err.name === 'AbortError') {
        updateItem(item.id, { status: FILE_STATUS.paused });
      } else {
        updateItem(item.id, { status: FILE_STATUS.error, error: err.message });
      }
    } finally {
      uploadingCountRef.current -= 1;
      processQueueRef.current();
    }
  };

  processQueueRef.current = () => {
    while (uploadingCountRef.current < CONCURRENT_LIMIT && queueRef.current.length > 0) {
      const next = queueRef.current.shift();
      uploadFileRef.current(next);
    }
  };

  const processQueue = useCallback(() => {
    processQueueRef.current();
  }, []);

  const addFiles = useCallback((newFiles) => {
    const pdfs = Array.from(newFiles).filter((f) => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
    if (pdfs.length === 0) return;
    const existingNames = new Set(fileItems.map((i) => i.file.name));
    const unique = pdfs
      .filter((f) => !existingNames.has(f.name))
      .map(createFileItem);
    if (unique.length === 0) return;
    setFileItems((prev) => [...prev, ...unique]);
    if (isStartedRef.current) {
      queueRef.current.push(...unique);
      processQueue();
    }
  }, [fileItems, processQueue]);

  const handleFileSelect = (e) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const readEntriesRecursive = (dirReader) =>
    new Promise((resolve) => {
      const allEntries = [];
      const readBatch = () => {
        dirReader.readEntries((batch) => {
          if (batch.length === 0) {
            resolve(allEntries);
          } else {
            allEntries.push(...batch);
            readBatch();
          }
        });
      };
      readBatch();
    });

  const collectFilesFromEntry = async (entry) => {
    if (entry.isFile) {
      return new Promise((resolve) => entry.file(resolve));
    }
    if (entry.isDirectory) {
      const entries = await readEntriesRecursive(entry.createReader());
      const nested = await Promise.all(entries.map(collectFilesFromEntry));
      return nested.flat();
    }
    return [];
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const items = e.dataTransfer.items;
    if (items && items.length > 0 && typeof items[0].webkitGetAsEntry === 'function') {
      const entries = Array.from(items).map((i) => i.webkitGetAsEntry()).filter(Boolean);
      const results = await Promise.all(entries.map(collectFilesFromEntry));
      addFiles(results.flat());
    } else {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleResume = useCallback((item) => {
    const resumed = { ...item, status: FILE_STATUS.queued, progress: 0 };
    queueRef.current.push(resumed);
    setFileItems((prev) => prev.map((i) => (i.id === item.id ? resumed : i)));
    processQueue();
  }, [processQueue]);

  const handleRetry = useCallback((item) => {
    const retried = { ...item, status: FILE_STATUS.queued, progress: 0, error: null };
    queueRef.current.push(retried);
    setFileItems((prev) => prev.map((i) => (i.id === item.id ? retried : i)));
    processQueue();
  }, [processQueue]);

  const handleRemove = (item) => {
    const controller = abortControllersRef.current.get(item.id);
    if (controller) controller.abort();
    abortControllersRef.current.delete(item.id);
    queueRef.current = queueRef.current.filter((q) => q.id !== item.id);
    setFileItems((prev) => prev.filter((i) => i.id !== item.id));
  };

  const handleClearDone = () => {
    setFileItems((prev) => prev.filter((i) => i.status !== FILE_STATUS.done));
  };

  const handleStartAll = useCallback(() => {
    isStartedRef.current = true;
    setIsStarted(true);
    const pending = fileItems.filter((i) => i.status === FILE_STATUS.queued || i.status === FILE_STATUS.paused);
    queueRef.current.push(...pending);
    processQueue();
  }, [fileItems, processQueue]);

  const handlePauseAll = useCallback(() => {
    queueRef.current = [];
  }, []);

  const handleResumeAll = useCallback(() => {
    const pending = fileItems.filter((i) => i.status === FILE_STATUS.queued || i.status === FILE_STATUS.paused);
    queueRef.current.push(...pending);
    processQueue();
  }, [fileItems, processQueue]);

  const doneCount = fileItems.filter((i) => i.status === FILE_STATUS.done).length;
  const errorCount = fileItems.filter((i) => i.status === FILE_STATUS.error).length;
  const activeCount = fileItems.filter((i) => i.status === FILE_STATUS.uploading).length;
  const queuedCount = fileItems.filter((i) => i.status === FILE_STATUS.queued).length;
  const pausedCount = fileItems.filter((i) => i.status === FILE_STATUS.paused).length;
  const hasPending = queuedCount > 0 || activeCount > 0 || pausedCount > 0;
  const overallProgress = fileItems.length > 0
    ? Math.round(fileItems.reduce((sum, i) => sum + i.progress, 0) / fileItems.length)
    : 0;

  useEffect(() => {
    if (isStarted && !hasPending && fileItems.length > 0) {
      isStartedRef.current = false;
      setIsStarted(false);
    }
  }, [isStarted, hasPending, fileItems.length]);

  const badgeStyle = { fontSize: '11px', padding: '3px 9px', borderRadius: '20px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' };

  const getStatusBadge = (item) => {
    if (item.status === FILE_STATUS.queued) {
      return <span style={{ ...badgeStyle, background: '#f0f0f0', color: '#6c757d' }}>{t('Waiting')}</span>;
    }
    if (item.status === FILE_STATUS.uploading) {
      return (
        <span style={{ ...badgeStyle, background: '#e7f3ff', color: '#0d6efd' }}>
          <Spinner animation="border" style={{ width: '10px', height: '10px', borderWidth: '1.5px', flexShrink: 0 }} />
          {item.progress}%
        </span>
      );
    }
    if (item.status === FILE_STATUS.paused) {
      return <span style={{ ...badgeStyle, background: '#fff3cd', color: '#856404' }}>{t('Paused')}</span>;
    }
    if (item.status === FILE_STATUS.done) {
      return <span style={{ ...badgeStyle, background: '#d1e7dd', color: '#0a3622' }}><FiCheckCircle size={11} />{t('Done')}</span>;
    }
    if (item.status === FILE_STATUS.error) {
      return <span style={{ ...badgeStyle, background: '#f8d7da', color: '#842029' }} title={item.error}><FiAlertCircle size={11} />{t('Failed')}</span>;
    }
    return null;
  };

  return (
    <Card>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-0">{t('Bulk Upload Bills')}</h5>
            <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
              {t('Upload PDF bills in bulk. Files are automatically parsed and saved by the system.')}
            </p>
          </div>
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            {doneCount > 0 && (
              <Button variant="outline-secondary" size="sm" onClick={handleClearDone}>
                {t('Clear done')}
              </Button>
            )}
            {!isStarted && fileItems.length > 0 && (
              <Button variant="success" size="sm" onClick={handleStartAll} disabled={!hasPending}>
                <FiPlay className="mr-1" style={{ verticalAlign: 'middle' }} />
                {t('Start Upload')}
              </Button>
            )}
            {isStarted && activeCount > 0 && (
              <Button variant="outline-warning" size="sm" onClick={handlePauseAll}>
                <FiPause className="mr-1" style={{ verticalAlign: 'middle' }} />
                {t('Pause All')}
              </Button>
            )}
            {isStarted && activeCount === 0 && hasPending && (
              <Button variant="outline-success" size="sm" onClick={handleResumeAll}>
                <FiPlay className="mr-1" style={{ verticalAlign: 'middle' }} />
                {t('Resume All')}
              </Button>
            )}
            <Button
              variant="outline-dark"
              size="sm"
              onClick={() => folderInputRef.current?.click()}
            >
              <FiUploadCloud className="mr-1" style={{ verticalAlign: 'middle' }} />
              {t('Select Folder')}
            </Button>
            <Button
              variant="dark"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              <FiUploadCloud className="mr-1" style={{ verticalAlign: 'middle' }} />
              {t('Select Files')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <input
              ref={folderInputRef}
              type="file"
              accept=".pdf,application/pdf"
              multiple
              webkitdirectory=""
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </div>
        </div>

        <div
          className={`border rounded p-4 text-center mb-3 ${isDragging ? 'border-dark bg-light' : 'border-dashed'}`}
          style={{ borderStyle: 'dashed', cursor: 'pointer', transition: 'background 0.2s' }}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <FiUploadCloud size={28} className="text-muted mb-2" />
          <div className="text-muted" style={{ fontSize: '14px' }}>
            {isDragging ? t('Drop files or folders here') : t('Drag & drop PDF files or folders here, or click to select')}
          </div>
        </div>

        {fileItems.length > 0 && (
          <>
            <div className="d-flex align-items-center mb-2" style={{ gap: '12px', fontSize: '12px', color: '#6c757d' }}>
              <span style={{ fontWeight: 500 }}>{fileItems.length} {t('files')}</span>
              {activeCount > 0 && <span style={{ color: '#0d6efd' }}>{t('Uploading')}: {activeCount}</span>}
              {queuedCount > 0 && <span>{t('Waiting')}: {queuedCount}</span>}
              {pausedCount > 0 && <span style={{ color: '#856404' }}>{t('Paused')}: {pausedCount}</span>}
              {doneCount > 0 && <span style={{ color: '#198754' }}>{t('Done')}: {doneCount}</span>}
              {errorCount > 0 && <span style={{ color: '#dc3545' }}>{t('Failed')}: {errorCount}</span>}
            </div>

            {isStarted && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span style={{ fontSize: '12px', color: '#6c757d', fontWeight: 500 }}>{t('Overall Progress')}</span>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: overallProgress === 100 ? '#198754' : '#0d6efd' }}>{overallProgress}%</span>
                </div>
                <div style={{ height: '6px', background: '#e9ecef', borderRadius: '6px', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${overallProgress}%`,
                      background: overallProgress === 100 ? '#198754' : '#0d6efd',
                      borderRadius: '6px',
                      transition: 'width 0.4s ease'
                    }}
                  />
                </div>
              </div>
            )}

            <div style={{ border: '1px solid #e9ecef', borderRadius: '8px', maxHeight: '420px', overflowY: 'auto' }}>
              <div style={{ position: 'sticky', top: 0, zIndex: 1, display: 'grid', gridTemplateColumns: '1fr 72px 130px 56px', padding: '7px 12px', background: '#f8f9fa', borderBottom: '1px solid #e9ecef', fontSize: '11px', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <span>{t('File')}</span>
                <span style={{ textAlign: 'right', paddingRight: '12px' }}>{t('Size')}</span>
                <span style={{ textAlign: 'center' }}>{t('Status')}</span>
                <span></span>
              </div>

              {fileItems.map((item) => (
                <div
                  key={item.id}
                  style={{ position: 'relative', display: 'grid', gridTemplateColumns: '1fr 72px 130px 56px', padding: '7px 12px', borderBottom: '1px solid #f5f5f5', alignItems: 'center', background: item.status === FILE_STATUS.error ? '#fffafa' : 'white' }}
                >
                  {item.status === FILE_STATUS.uploading && (
                    <div style={{ position: 'absolute', bottom: 0, left: 0, height: '2px', width: `${item.progress}%`, background: '#0d6efd', transition: 'width 0.4s ease', borderRadius: '0 2px 2px 0' }} />
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, paddingRight: '8px' }}>
                    <span style={{ flexShrink: 0, width: '26px', height: '26px', background: '#fff0f0', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FiFileText size={13} style={{ color: '#dc3545' }} />
                    </span>
                    <span style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#212529' }} title={item.file.name}>
                      {item.file.name}
                    </span>
                  </div>
                  <span style={{ fontSize: '12px', color: '#6c757d', textAlign: 'right', paddingRight: '12px' }}>
                    {formatFileSize(item.file.size)}
                  </span>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {getStatusBadge(item)}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '2px' }}>
                    {item.status === FILE_STATUS.paused && (
                      <Button variant="link" size="sm" className="p-1 text-muted" title={t('Resume')} onClick={() => handleResume(item)}>
                        <FiPlay size={14} />
                      </Button>
                    )}
                    {item.status === FILE_STATUS.error && (
                      <Button variant="link" size="sm" className="p-1 text-muted" title={t('Retry')} onClick={() => handleRetry(item)}>
                        <FiRefreshCw size={14} />
                      </Button>
                    )}
                    <Button
                      variant="link"
                      size="sm"
                      className="p-1 text-muted"
                      title={item.status === FILE_STATUS.uploading ? t('Cancel') : t('Remove')}
                      onClick={() => handleRemove(item)}
                    >
                      <FiX size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default BulkUploadTab;
